import React, { useEffect, useState } from 'react';
import { ReactComponent as StepDeleteIcon } from '../../../../../../assets/svg/stepDeleteIcon.svg';
import { Autocomplete, IconButton, TextField } from '@mui/material';
import { IProcessingMergeJoinInnerBlock } from './processingMergeJoinInnerBlock.models';
import {
  columnsAutocompleteLiStyles,
  columnsAutocompletePlaceholderStyles,
  columnsAutocompleteStyles,
  removeIconStyles,
  spanStyles,
  wholeStyles
} from './processingMergeJopinInnerBlock.styles';
import { removeLastExtension } from '../../../../../../helpers/removeExtension';
import { processingMergeJoinEmptyBlock } from './processingMergeJoinInnerBlock.constants';

const ProcessingMergeJoinInnerBlock: React.FC<IProcessingMergeJoinInnerBlock> = ({
  setIsDuplicateChecked,
  selectedMergeJoinOption,
  updateSingleActionBody,
  data,
  summaryData,
  processingMergeInputData,
  setBlocks,
  block,
  blocks,
  element,
  deleteBlock,
  onOptionsExhausted
}) => {
  const [selectedFirstColumns, setSelectedFirstColumns] = useState<string[]>(
    block.singleActionBody.initialDataset?.length > 0 ? block.singleActionBody.initialDataset : []
  );
  const [selectedSecondColumns, setSelectedSecondColumns] = useState<string[]>(
    block.singleActionBody.secondDataset?.length > 0 ? block.singleActionBody.secondDataset : []
  );

  // Find the index of the current block
  const innerBlockIndex = blocks.findIndex((b: any) => b.id === block?.id);
  const isFirstRow = innerBlockIndex === 0; // Check if the current block is the first row

  // Determine if all fields are filled (both first and second columns selected)
  const allFieldsAreFilled = selectedFirstColumns.length > 0 && selectedSecondColumns.length > 0;

  // Filter columns based on selected columns in all blocks
  const getFilteredColumns = (columns: string[], dataset: any) => {
    // Ensure initialDataset and secondDataset are arrays before flatMap
    const usedInitialColumns = blocks
      .filter((b: any) => b.id !== block.id) // Exclude the current block
      .flatMap((b: any) => (Array.isArray(b.singleActionBody.initialDataset) ? b.singleActionBody.initialDataset : [])); // Get all selected initialDataset columns from other blocks

    const usedSecondColumns = blocks
      .filter((b: any) => b.id !== block.id) // Exclude the current block
      .flatMap((b: any) => (Array.isArray(b.singleActionBody.secondDataset) ? b.singleActionBody.secondDataset : [])); // Get all selected secondDataset columns from other blocks

    // Filter columns for the current block based on the input type (initialDataset or secondDataset)
    return createDatasetOptions(dataset).filter((option) => {
      // If it's the initialDataset (first input), do not show columns already selected in the initialDataset of other rows
      if (columns === selectedFirstColumns) {
        return !usedInitialColumns.includes(option.label); // Exclude used initialDataset columns
      }

      // If it's the secondDataset (second input), do not show columns already selected in the secondDataset of other rows
      return !usedSecondColumns.includes(option.label); // Exclude used secondDataset columns
    });
  };

  // Create dataset options with ids
  const createDatasetOptions = (dataset: any) => {
    return Object.keys(dataset.preview_data)
      .filter((key) => dataset.preview_data[key].message_instead === null) // Filter out keys with non-null message_instead
      .map((key, index) => ({
        id: index + 1,
        label: key
      }));
  };

  const initialDatasetOptionsWithIds = getFilteredColumns(selectedFirstColumns, data.InitialDataset);
  const secondDatasetOptionsWithIds = getFilteredColumns(selectedSecondColumns, data.SecondDataset);

  const isPlusDisabled = initialDatasetOptionsWithIds.length === 1 || secondDatasetOptionsWithIds.length === 1;

  const handleFirstColumnsChange = (_: any, newValue: { id: number; label: string } | null) => {
    if (newValue) {
      setSelectedFirstColumns([newValue.label]); // Set it as the selected value
    } else {
      setSelectedFirstColumns([]); // Clear if null
    }
  };

  const handleSecondColumnsChange = (_: any, newValue: { id: number; label: string } | null) => {
    if (newValue) {
      setSelectedSecondColumns([newValue.label]); // Set it as the selected value
    } else {
      setSelectedSecondColumns([]); // Clear if null
    }
  };

  useEffect(() => {
    const newActionBody = {
      initialDataset: selectedFirstColumns,
      secondDataset: selectedSecondColumns,
      isCompleted: allFieldsAreFilled // Mark the block as completed if both columns are selected
    };
    const updatedBlocks = updateSingleActionBody(blocks, block.id, newActionBody);
    setBlocks(updatedBlocks);
  }, [selectedFirstColumns, selectedSecondColumns]);

  useEffect(() => {
    onOptionsExhausted(isPlusDisabled); // Notify the parent if options are exhausted
  }, [initialDatasetOptionsWithIds, secondDatasetOptionsWithIds]);

  useEffect(() => {
    setIsDuplicateChecked(false);
    setSelectedFirstColumns([]);
    setSelectedSecondColumns([]);
  }, [selectedMergeJoinOption]);

  return (
    <div style={wholeStyles}>
      <div>
        {/* Show file name in the first row */}
        {isFirstRow && <span style={spanStyles}>{removeLastExtension(summaryData.filename)}</span>}
        <Autocomplete
          value={initialDatasetOptionsWithIds.find((option) => selectedFirstColumns.includes(option.label)) || null} // Use a single object or null
          options={initialDatasetOptionsWithIds}
          onChange={(_, newValue: { id: number; label: string } | null) => handleFirstColumnsChange(_, newValue)}
          sx={columnsAutocompleteStyles(blocks)}
          renderOption={(props, option) => (
            <li {...props} style={columnsAutocompleteLiStyles}>
              {option.label}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="select column"
              label=""
              InputProps={{
                ...params.InputProps,
                style: columnsAutocompleteLiStyles
              }}
              sx={columnsAutocompletePlaceholderStyles}
            />
          )}
          componentsProps={{
            clearIndicator: {
              onClick: () => setSelectedFirstColumns([]) // Clear selections
            }
          }}
        />
      </div>

      <div>
        {/* Show file name in the second row */}
        {isFirstRow && (
          <span style={spanStyles}>
            {processingMergeInputData.dataset.length > 0
              ? removeLastExtension(processingMergeInputData.dataset)
              : removeLastExtension(processingMergeInputData?.uploadedFileName)}
          </span>
        )}
        <Autocomplete
          value={secondDatasetOptionsWithIds.find((option) => selectedSecondColumns.includes(option.label)) || null} // Use a single object or null
          options={secondDatasetOptionsWithIds}
          onChange={(_, newValue: { id: number; label: string } | null) => handleSecondColumnsChange(_, newValue)}
          sx={columnsAutocompleteStyles(blocks)}
          renderOption={(props, option) => (
            <li {...props} style={columnsAutocompleteLiStyles}>
              {option.label}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="select column"
              label=""
              InputProps={{
                ...params.InputProps,
                style: columnsAutocompleteLiStyles
              }}
              sx={columnsAutocompletePlaceholderStyles}
            />
          )}
          componentsProps={{
            clearIndicator: {
              onClick: () => setSelectedSecondColumns([]) // Clear selections
            }
          }}
        />
      </div>

      {/* Delete button (appears if there's more than one block) */}
      {blocks.length > 1 && (
        <IconButton style={removeIconStyles(element)} onClick={() => deleteBlock(element)} disableRipple={true}>
          <StepDeleteIcon />
        </IconButton>
      )}
    </div>
  );
};

export default ProcessingMergeJoinInnerBlock;
